import { NavLink } from "react-router-dom";
import { ClipboardPlus, ListChecks, FileText, ShieldCheck, Undo2, Settings, LogOut, Tags, Users, LayoutDashboard, Calculator, ScanSearch } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { ROLE_LABELS } from "../lib/labels";
import { NotificationBell } from "./Notifications";
import { HelpChat } from "./HelpChat";
import { LangSwitch, useLang } from "../lib/i18n";

const NAV = [
  { to: "/declarer", label: "Déclarer", icon: ClipboardPlus, roles: ["soustraitant", "chauffeur"] },
  { to: "/prestations", label: "Mes prestations", icon: ListChecks, roles: ["soustraitant", "admin", "gestionnaire"] },
  { to: "/proformas", label: "Mes pro formas", icon: FileText, roles: ["soustraitant", "admin", "gestionnaire"] },
  { to: "/controles", label: "Contrôles", icon: ShieldCheck, roles: ["admin", "gestionnaire"] },
  { to: "/pm-reports", label: "PM Reports", icon: ScanSearch, roles: ["admin", "gestionnaire"] },
  { to: "/tableau-de-bord", label: "Tableau de bord", icon: LayoutDashboard, roles: ["admin", "gestionnaire"] },
  { to: "/retours", label: "Retours", icon: Undo2, roles: ["admin", "gestionnaire", "soustraitant"] },
  { to: "/tarifs", label: "Mes tarifs", icon: Tags, roles: ["soustraitant"] },
  { to: "/chauffeurs", label: "Mes chauffeurs", icon: Users, roles: ["soustraitant"] },
  { to: "/simulation", label: "Simulation", icon: Calculator, roles: ["admin", "gestionnaire", "soustraitant"] },
  { to: "/parametres", label: "Paramètres", icon: Settings, roles: ["admin", "gestionnaire"] },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { t, tr } = useLang();
  const items = NAV.filter((n) => n.roles.includes(user.role));
  return (
    <div className="min-h-screen pb-24 lg:pb-8">
      <header className="tl-stripe text-white">
        <div className="max-w-[1600px] mx-auto px-4 py-3 flex items-center gap-4">
          <div className="flex items-baseline gap-2">
            <span className="tl-display text-2xl font-extrabold uppercase tracking-tight" data-testid="app-brand">TourLink</span>
            <span className="text-xs text-amber-400 font-semibold uppercase tracking-widest hidden 2xl:inline">Clôture tournée</span>
          </div>
          <nav className="hidden lg:flex gap-0.5 ml-2 min-w-0 overflow-x-auto">
            {items.map((n) => (
              <NavLink key={n.to} to={n.to} data-testid={`nav-${n.to.slice(1)}`}
                className={({ isActive }) => `px-2.5 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${isActive ? "bg-amber-600 text-white" : "text-slate-200 hover:bg-white/10"}`}>
                {t(`nav.${n.to}`)}
              </NavLink>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-2 shrink-0">
            <div className="text-right leading-tight min-w-0 max-w-[120px] sm:max-w-[200px]">
              <div className="text-sm font-semibold truncate" data-testid="current-user-name" title={user.name}>{user.name}</div>
              <div className="text-[11px] text-slate-300 uppercase tracking-wider truncate">{tr(ROLE_LABELS[user.role])}</div>
            </div>
            <LangSwitch />
            <NotificationBell />
            <button onClick={logout} className="flex items-center gap-1.5 px-3 py-2 rounded-full hover:bg-white/10 text-sm font-semibold" aria-label={t("nav.logout")} data-testid="logout-button">
              <LogOut size={20} /><span className="hidden xl:inline whitespace-nowrap">{t("nav.logout")}</span>
            </button>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-3 sm:px-4 py-6">{children}</main>
      <HelpChat />
      {items.length > 1 && <nav className="lg:hidden fixed bottom-0 inset-x-0 bg-white border-t border-slate-200 flex overflow-x-auto z-40 pb-[env(safe-area-inset-bottom)]" data-testid="mobile-nav">
        {items.map((n) => (
          <NavLink key={n.to} to={n.to} data-testid={`mnav-${n.to.slice(1)}`}
            className={({ isActive }) => `flex-1 min-w-[68px] flex flex-col items-center py-2 text-[10px] font-semibold ${isActive ? "text-amber-700" : "text-slate-500"}`}>
            <n.icon size={22} />
            <span className="mt-0.5 text-center leading-tight">{t(`nav.${n.to}`)}</span>
          </NavLink>
        ))}
      </nav>}
    </div>
  );
}
